import type { Domain } from "@/types/domain";
import type { SortDirection } from "./types";
import { DATE_COLUMN_KEYS } from "./constants";

export const sortDomains = (
  domains: Domain[],
  sortColumn: string | null,
  sortDirection: SortDirection,
): Domain[] => {
  if (!sortColumn || !sortDirection) {
    return domains;
  }

  return [...domains].sort((a, b) => {
    const key = sortColumn as keyof Domain;
    const aValue = a[key];
    const bValue = b[key];

    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    // Handle different data types
    let comparison = 0;

    // Check if values are dates
    if ((DATE_COLUMN_KEYS as readonly string[]).includes(sortColumn)) {
      const toTime = (v: string | number | boolean): number => {
        if (typeof v === "boolean") return NaN;
        return new Date(v).getTime();
      };
      const aDate = toTime(aValue);
      const bDate = toTime(bValue);
      if (!isNaN(aDate) && !isNaN(bDate)) {
        comparison = aDate - bDate;
      } else {
        // Fallback to string comparison if dates are invalid
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        comparison = aStr.localeCompare(bStr);
      }
    } else if (typeof aValue === "number" && typeof bValue === "number") {
      comparison = aValue - bValue;
    } else if (typeof aValue === "boolean" && typeof bValue === "boolean") {
      comparison = aValue === bValue ? 0 : aValue ? 1 : -1;
    } else {
      // String comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      comparison = aStr.localeCompare(bStr);
    }

    return sortDirection === "asc" ? comparison : -comparison;
  });
};
